// «Речь» (admin-only, EXPERIMENTAL) — transcript of the current VOD + Gemini over speech.
//
// Transcript: produced on the operator's PC (tools/transcribe/transcribe-vod.mjs), stored in
// GCS, served by cloud-run-api `GET /transcript` (admin-only). Held ONLY in a transient signal —
// never in SavedState / IDB project state / localStorage / Cloud Sync (MBs of text would bust
// the lite-localStorage fallback and the 1MB Firestore doc). Re-fetched on every VOD open.
//
// Results (interest segments, AI-search hits) are ordinary Highlights with source
// 'transcript-ai' / 'transcript-search' → cards, pin, clip editor, download and sync for free.
//
// Non-admins: the load effect returns before any request; nothing else is reachable from UI.
import { computed, effect, inject, Injectable, signal, untracked, WritableSignal } from '@angular/core';
import { TwitchChatStateService, SavedState } from './twitch-chat-state.service.js';
import { TwitchChatService } from './twitch-chat.service.js';
import { FirebaseService } from './firebase.service.js';
import { LibraryService } from './library.service.js';
import { SyncService } from './sync.service.js';
import { checkDemoMode } from './demo.service.js';
import type { ActivityBin, Highlight } from '../models/twitch.model';
import {
  TRANSCRIPT_CATEGORIES, TRANSCRIPT_CATEGORY_KEYS, TranscriptCategory, TranscriptDoc, TranscriptModelKey,
} from '../models/transcript.model.js';
import {
  categoriesPayload, chatPayload, dedupeByOverlap, lineIndexAt, lineRangeIn, mergeTranscriptHighlights, planCores,
  planCoresInRange, planSearch, TranscriptChunk,
} from '../utils/transcript.js';
import { TimeRegion, isVisibleInRegion, overlapsRegion } from '../utils/region.js';
import { displayKeywords, matchText, parseSearchQuery } from '../utils/search-query.js';

export type TranscriptStatus = 'idle' | 'loading' | 'ready' | 'missing' | 'error';

export interface TranscriptRunState {
  runId: string;
  running: boolean;
  done: number;
  total: number;
  failed: number;
  results: number;
  tokens: number;
  model: TranscriptModelKey;
  error?: string;
}

interface RawSegment {
  startLine: number; endLine: number; startTime: number; endTime: number;
  category: TranscriptCategory; tag?: string; score: number; title: string; summary: string; quote: string;
  model: string; withChat: boolean;
}

interface RawSearchHit {
  startLine: number; endLine: number; startTime: number; endTime: number;
  confidence: number; description: string; quote: string; model: string; withChat: boolean;
}

/** A run in flight — its progress lives here while it runs, whichever VOD is open. */
interface LiveRun {
  ctrl: AbortController;
  state: TranscriptRunState;
}

/** Remembered between «Найти» and «Повторить неудачные» — only failed chunks are re-sent. */
interface PendingRun<T> {
  videoId: string;
  createdAt: string;
  query?: string;
  regionKey: string;               // «область интереса» the plan was made for ('' = whole VOD)
  withChat: boolean;               // «+ чат» of the original run — a retry sends the same input
  chunks: TranscriptChunk[];       // full plan (chunkIndex/chunkCount stay stable on retry)
  failed: TranscriptChunk[];
  collected: T[];
  tokens: number;
}

const CHUNK_CONCURRENCY = 4;
const CLIENT_TIMEOUT_MS = 290 * 1000;
const RETRY_DELAY_MS = 15 * 1000;
const CLIP_PAD_SEC = 1;
// The server shows the model 5 min before / 10 min after each window (lib/transcript.js HALO_*;
// search windows: 30 min after) — chat is sent for that span plus a minute of slack.
const CHAT_BEFORE_SEC = 6 * 60;
const CHAT_AFTER_SEC = 11 * 60;
const SEARCH_CHAT_AFTER_SEC = 31 * 60;
const SEGMENTS_PER_HOUR = 15;
const SEARCH_RESULT_CAP = 15;
const SEARCH_MIN_CLIP_SEC = 10;
const BIN_SEC = 15;

class ChunkError extends Error {
  constructor(message: string, readonly status: number, readonly code?: string, readonly retryable = false) {
    super(message);
  }
}

function formatHms(totalSeconds: number): string {
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = Math.floor(totalSeconds % 60);
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

const regionKeyOf = (r: TimeRegion | null) => (r ? `${r.inSec}-${r.outSec}` : '');

function newRunId(): string {
  return (globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`);
}

@Injectable({ providedIn: 'root' })
export class TranscriptService {
  private state = inject(TwitchChatStateService);
  private chat = inject(TwitchChatService);
  private firebase = inject(FirebaseService);
  private library = inject(LibraryService);
  private sync = inject(SyncService);

  readonly isAdmin = computed(() => this.firebase.userProfile()?.role === 'admin' && !checkDemoMode());

  readonly status = signal<TranscriptStatus>('idle');
  readonly errorMessage = signal('');
  readonly transcript = signal<TranscriptDoc | null>(null);
  readonly starts = computed(() => {
    const t = this.transcript();
    return t ? Float64Array.from(t.lines, l => l[0]) : new Float64Array(0);
  });

  // --- Instant text search (Moment Search syntax + russianStem) ---
  readonly textInput = signal('');
  readonly textQuery = signal('');
  readonly textExact = signal(false);
  readonly textMatches = computed(() => {
    const doc = this.transcript();
    const q = this.textQuery().trim();
    if (!doc || !q) return null;
    const parsed = parseSearchQuery(q);
    const exact = this.textExact();
    if (parsed.positive.length === 0 && parsed.phrases.length === 0) {
      return { lines: [] as number[], set: new Set<number>(), keywords: [] as string[], exact, invalid: true };
    }
    const lines: number[] = [];
    for (let i = 0; i < doc.lines.length; i++) {
      if (matchText(doc.lines[i][2], parsed, exact).matched) lines.push(i);
    }
    return { lines, set: new Set(lines), keywords: displayKeywords(parsed), exact, invalid: false };
  });

  // --- Gemini runs ---
  readonly segModel = signal<TranscriptModelKey>('pro');
  readonly searchModel = signal<TranscriptModelKey>('flash');
  readonly aiQueryInput = signal('');
  readonly segRun = signal<TranscriptRunState | null>(null);
  readonly searchRun = signal<TranscriptRunState | null>(null);
  /**
   * Runs in flight by videoId. A run keeps going after a VOD switch (its results route to its VOD);
   * coming back to that VOD shows its progress again and its Cancel works, and a second run of the
   * same kind on that VOD can't start meanwhile (it billed Gemini twice and raced the first one's apply).
   */
  private readonly liveSeg = new Map<string, LiveRun>();
  private readonly liveSearch = new Map<string, LiveRun>();
  // Pending retries belong to the VOD that's open: only a run of the open VOD writes them (a
  // background run finishing later used to overwrite them — «Повторить неудачные» then re-sent everything).
  private segPending: PendingRun<RawSegment> | null = null;
  private searchPending: PendingRun<RawSearchHit> | null = null;
  readonly segCanRetry = signal(false);
  readonly searchCanRetry = signal(false);

  /** «+ чат»: send the chat (as filtered on the chat tab, no nicknames) along with the speech. */
  readonly useChat = signal(false);
  /**
   * Chat is only local: a VOD synced from another device has none until it's re-downloaded. While
   * the (parallel) download runs, whole stretches are still empty — the model would read them as
   * a silent chat, so wait for it.
   */
  readonly chatAvailable = computed(() => this.state.filteredComments().length > 0 && !this.state.isChatDownloading());

  // Region-filtered like the other tabs' lists (pinned always shown) — UI-only consumers.
  readonly segments = computed(() => {
    const region = this.state.activeRegion();
    return this.state.highlights().filter(h => h.source === 'transcript-ai' && !h.isDeleted && isVisibleInRegion(h, region));
  });
  readonly searchHits = computed(() => {
    const region = this.state.activeRegion();
    return this.state.highlights().filter(h => h.source === 'transcript-search' && !h.isDeleted && isVisibleInRegion(h, region));
  });
  readonly categoryCounts = computed(() => {
    const counts: Record<TranscriptCategory, number> = { funny: 0, story: 0, emotion: 0, opinion: 0, gameplay: 0, other: 0 };
    for (const h of this.segments()) if (h.transcriptCategory) counts[h.transcriptCategory]++;
    return counts;
  });

  private lastLoadedVideoId: string | null = null;
  private loadToken = 0;

  constructor() {
    // Load on VOD change. Non-admins never get past the guard → zero requests.
    effect(() => {
      const vid = this.state.videoId();
      const allowed = !!this.firebase.currentUser() && this.isAdmin();
      untracked(() => this.onVodChanged(vid != null && vid !== '' ? String(vid) : null, allowed));
    });
  }

  private onVodChanged(videoId: string | null, allowed: boolean): void {
    if (!allowed || !videoId) {
      if (this.lastLoadedVideoId !== null) this.resetForVod(null);
      return;
    }
    if (this.lastLoadedVideoId === videoId) return;
    this.resetForVod(videoId);
    void this.load(videoId);
  }

  private resetForVod(videoId: string | null): void {
    this.lastLoadedVideoId = videoId;
    this.loadToken++;
    this.transcript.set(null);
    this.status.set('idle');
    this.errorMessage.set('');
    this.textInput.set('');
    this.textQuery.set('');
    // Runs keep going in the background (results route to their original VOD) — the progress line
    // shows this VOD's own run, if it has one in flight; the retry UI is reset.
    this.segRun.set((videoId && this.liveSeg.get(videoId)?.state) || null);
    this.searchRun.set((videoId && this.liveSearch.get(videoId)?.state) || null);
    this.segCanRetry.set(false);
    this.searchCanRetry.set(false);
  }

  reload(): void {
    const vid = this.state.videoId();
    if (vid != null && this.isAdmin()) void this.load(String(vid));
  }

  private async load(videoId: string): Promise<void> {
    const token = ++this.loadToken;
    this.status.set('loading');
    this.errorMessage.set('');
    try {
      const headers = await this.firebase.buildCloudRunHeaders();
      if (!headers) throw new Error('Нужно войти в аккаунт.');
      const res = await fetch(`${this.firebase.cloudRunApiUrl}/transcript?vodId=${encodeURIComponent(videoId)}`, { headers });
      if (token !== this.loadToken) return;
      if (res.status === 404) {
        this.transcript.set(null);
        this.status.set('missing');
        return;
      }
      if (!res.ok) {
        const payload = await res.json().catch(() => ({}));
        throw new Error(payload.error || `HTTP ${res.status}`);
      }
      const doc = await res.json() as TranscriptDoc;
      if (token !== this.loadToken) return;
      if (doc?.v !== 1 || !Array.isArray(doc.lines)) throw new Error('Неизвестный формат транскрипта');
      this.transcript.set(doc);
      this.status.set('ready');
    } catch (e) {
      if (token !== this.loadToken) return;
      console.error('[transcript] load failed', e);
      this.status.set('error');
      this.errorMessage.set(e instanceof TypeError
        ? 'Не удалось подключиться. Попробуйте обновить страницу или сменить сеть.'
        : (e instanceof Error ? e.message : 'Не удалось загрузить транскрипт'));
    }
  }

  /** Line index under the playhead (−1 before the first line). */
  lineIndexAt(t: number): number {
    return lineIndexAt(this.starts(), t);
  }

  runTextSearch(): void {
    this.textQuery.set(this.textInput().trim());
  }

  clearTextSearch(): void {
    this.textInput.set('');
    this.textQuery.set('');
  }

  // ---------------------------------------------------------------------------
  // Interest segments
  // ---------------------------------------------------------------------------

  async runSegments(retryFailedOnly = false): Promise<void> {
    const doc = this.transcript();
    const videoId = this.lastLoadedVideoId;
    if (!doc || !videoId || this.liveSeg.has(videoId)) return;

    const model = this.segModel();
    const region = this.state.activeRegion();
    const regionKey = regionKeyOf(region);
    const pending = this.segPending;
    const resume = retryFailedOnly && pending && pending.videoId === videoId && pending.createdAt === doc.createdAt
      && pending.regionKey === regionKey;
    const chunks = resume ? pending!.chunks : this.planRun(doc, region);
    if (chunks.length === 0) {
      this.segRun.set({ runId: '', running: false, done: 0, total: 0, failed: 0, results: 0, tokens: 0, model, error: 'В выбранной области нет речи.' });
      return;
    }
    const toRun = resume ? pending!.failed : chunks;
    const collected: RawSegment[] = resume ? [...pending!.collected] : [];
    let tokens = resume ? pending!.tokens : 0;
    const withChat = resume ? pending!.withChat : this.useChat() && this.chatAvailable();
    const comments = withChat ? this.state.filteredComments() : [];

    const ctrl = new AbortController();
    this.segCanRetry.set(false);
    const context = this.captureContext();
    const bins = this.state.activityBins();
    const runId = newRunId();
    const live: LiveRun = {
      ctrl,
      state: { runId, running: true, done: chunks.length - toRun.length, total: chunks.length, failed: 0, results: 0, tokens, model },
    };
    this.liveSeg.set(videoId, live);
    this.segRun.set(live.state);

    try {
      const failed: TranscriptChunk[] = [];
      let changed = false;
      await this.runPool(toRun, async (c) => {
        try {
          const r = await this.postChunk('/transcript/analyze', {
            vodId: videoId, createdAt: doc.createdAt, model, context, runId,
            fromLine: c.fromLine, toLine: c.toLine, chunkIndex: chunks.indexOf(c), chunkCount: chunks.length,
            ...(withChat ? chatPayload(comments, c.startSec - CHAT_BEFORE_SEC, c.endSec + CHAT_AFTER_SEC) : {}),
          }, ctrl);
          for (const s of r.segments || []) collected.push({ ...s, model: r.model, withChat: !!r.withChat });
          tokens += r.usage?.totalTokens || 0;
        } catch (e) {
          if (ctrl.signal.aborted && !changed) return;
          if (e instanceof ChunkError && e.code === 'TRANSCRIPT_CHANGED') { changed = true; ctrl.abort(); return; }
          console.error('[transcript] analyze chunk failed', c, e);
          failed.push(c);
        } finally {
          this.bumpRun(this.liveSeg, this.segRun, videoId, runId, { tokens });
        }
      });

      if (changed) return this.onTranscriptChanged(this.segRun, runId, 'Транскрипт обновился во время анализа — запустите заново.');
      if (ctrl.signal.aborted) {
        this.segRun.update(r => r && r.runId === runId ? { ...r, running: false, error: 'Отменено' } : r);
        return;
      }

      // Cap follows the scanned span: the whole VOD, or just the region.
      const durationHours = region
        ? Math.max(0.25, (region.outSec - region.inSec) / 3600)
        : Math.max(1, (doc.durationSec || doc.lines[doc.lines.length - 1]?.[1] || 0) / 3600);
      const best = dedupeByOverlap(this.inRegion(collected, region), s => s.score)
        .slice(0, Math.ceil(durationHours * SEGMENTS_PER_HOUR));
      const highlights = best.map(s => this.segmentToHighlight(s, bins));
      await this.apply(videoId, highlights, 'transcript-ai', region);

      if (this.lastLoadedVideoId === videoId) {
        this.segPending = failed.length ? { videoId, createdAt: doc.createdAt, regionKey, withChat, chunks, failed, collected, tokens } : null;
        this.segCanRetry.set(failed.length > 0);
        this.segRun.update(r => r && r.runId === runId ? {
          ...r, running: false, failed: failed.length, results: highlights.length, tokens,
          error: failed.length ? `Не ответили фрагменты: ${failed.length} из ${chunks.length}` : undefined,
        } : r);
        if (this.state.activeTab() === 'speech' && this.state.speechMode() === 'segments') this.selectFirstIfNone();
      }
    } finally {
      if (this.liveSeg.get(videoId) === live) this.liveSeg.delete(videoId);
    }
  }

  cancelSegments(): void {
    if (this.lastLoadedVideoId) this.liveSeg.get(this.lastLoadedVideoId)?.ctrl.abort();
  }

  private segmentToHighlight(s: RawSegment, bins: ActivityBin[]): Highlight {
    const clipStart = Math.max(0, s.startTime - CLIP_PAD_SEC);
    const clipEnd = s.endTime + CLIP_PAD_SEC;
    return {
      source: 'transcript-ai',
      startTime: s.startTime,
      endTime: s.endTime,
      clipStartTime: clipStart,
      clipEndTime: clipEnd,
      estimatedSizeMb: this.chat.calculateEstimatedSize(clipEnd - clipStart),
      timestamp: formatHms(s.startTime),
      sentiment: TRANSCRIPT_CATEGORIES[s.category]?.label ?? 'Нейтральный',
      description: s.summary || s.title,
      sampleMessages: [],
      messageCount: this.countMessages(bins, s.startTime, s.endTime),
      isAnalyzed: true,
      isPinned: false,
      isDeleted: false,
      isPromoted: false,
      confidence: s.score,
      transcriptCategory: s.category,
      transcriptTag: s.tag || undefined,
      transcriptTitle: s.title,
      transcriptQuote: s.quote,
      transcriptLines: [s.startLine, s.endLine],
      transcriptModel: s.model,
      ...(s.withChat ? { transcriptWithChat: true } : {}),
    };
  }

  // ---------------------------------------------------------------------------
  // AI search over speech
  // ---------------------------------------------------------------------------

  async runAiSearch(retryFailedOnly = false): Promise<void> {
    const doc = this.transcript();
    const videoId = this.lastLoadedVideoId;
    if (!doc || !videoId || this.liveSearch.has(videoId)) return;

    const region = this.state.activeRegion();
    const regionKey = regionKeyOf(region);
    const pending = this.searchPending;
    const resume = retryFailedOnly && pending && pending.videoId === videoId && pending.createdAt === doc.createdAt
      && pending.regionKey === regionKey;
    const query = resume ? pending!.query! : this.aiQueryInput().trim();
    if (!query) return;
    const model = this.searchModel();
    const chunks = resume ? pending!.chunks : this.planSearchRun(doc, region);
    if (chunks.length === 0) {
      this.searchRun.set({ runId: '', running: false, done: 0, total: 0, failed: 0, results: 0, tokens: 0, model, error: 'В выбранной области нет речи.' });
      return;
    }
    const toRun = resume ? pending!.failed : chunks;
    const collected: RawSearchHit[] = resume ? [...pending!.collected] : [];
    let tokens = resume ? pending!.tokens : 0;
    const withChat = resume ? pending!.withChat : this.useChat() && this.chatAvailable();
    const comments = withChat ? this.state.filteredComments() : [];
    // One request (the usual case) has no context around it; split windows get the server's halo.
    const chatBefore = chunks.length > 1 ? CHAT_BEFORE_SEC : 60;
    const chatAfter = chunks.length > 1 ? SEARCH_CHAT_AFTER_SEC : 60;

    const ctrl = new AbortController();
    this.searchCanRetry.set(false);
    const context = this.captureContext();
    const bins = this.state.activityBins();
    const runId = newRunId();
    const live: LiveRun = {
      ctrl,
      state: { runId, running: true, done: chunks.length - toRun.length, total: chunks.length, failed: 0, results: 0, tokens, model },
    };
    this.liveSearch.set(videoId, live);
    this.searchRun.set(live.state);

    try {
      const failed: TranscriptChunk[] = [];
      let changed = false;
      await this.runPool(toRun, async (c) => {
        try {
          const r = await this.postChunk('/transcript/search', {
            vodId: videoId, createdAt: doc.createdAt, model, context, runId, query,
            fromLine: c.fromLine, toLine: c.toLine, chunkIndex: chunks.indexOf(c), chunkCount: chunks.length,
            ...(withChat ? chatPayload(comments, c.startSec - chatBefore, c.endSec + chatAfter) : {}),
          }, ctrl);
          for (const s of r.results || []) collected.push({ ...s, model: r.model, withChat: !!r.withChat });
          tokens += r.usage?.totalTokens || 0;
        } catch (e) {
          if (ctrl.signal.aborted && !changed) return;
          if (e instanceof ChunkError && e.code === 'TRANSCRIPT_CHANGED') { changed = true; ctrl.abort(); return; }
          console.error('[transcript] search chunk failed', c, e);
          failed.push(c);
        } finally {
          this.bumpRun(this.liveSearch, this.searchRun, videoId, runId, { tokens });
        }
      });

      if (changed) return this.onTranscriptChanged(this.searchRun, runId, 'Транскрипт обновился во время поиска — запустите заново.');
      if (ctrl.signal.aborted) {
        this.searchRun.update(r => r && r.runId === runId ? { ...r, running: false, error: 'Отменено' } : r);
        return;
      }

      const best = dedupeByOverlap(this.inRegion(collected, region), s => s.confidence)
        .sort((a, b) => b.confidence - a.confidence)
        .slice(0, SEARCH_RESULT_CAP);
      const highlights = best.map(s => this.searchHitToHighlight(s, query, bins));
      // A new query replaces every previous hit (query results don't mix), region or not.
      await this.apply(videoId, highlights, 'transcript-search');

      if (this.lastLoadedVideoId === videoId) {
        this.searchPending = failed.length ? { videoId, createdAt: doc.createdAt, query, regionKey, withChat, chunks, failed, collected, tokens } : null;
        this.searchCanRetry.set(failed.length > 0);
        this.searchRun.update(r => r && r.runId === runId ? {
          ...r, running: false, failed: failed.length, results: highlights.length, tokens,
          error: failed.length ? `Не ответили фрагменты: ${failed.length} из ${chunks.length}` : undefined,
        } : r);
        if (this.state.activeTab() === 'speech' && this.state.speechMode() === 'ai-search') this.selectFirstIfNone();
      }
    } finally {
      if (this.liveSearch.get(videoId) === live) this.liveSearch.delete(videoId);
    }
  }

  cancelAiSearch(): void {
    if (this.lastLoadedVideoId) this.liveSearch.get(this.lastLoadedVideoId)?.ctrl.abort();
  }

  private searchHitToHighlight(s: RawSearchHit, query: string, bins: ActivityBin[]): Highlight {
    const clipStart = Math.max(0, s.startTime - CLIP_PAD_SEC);
    // A single matched line can be 2-3s — give the clip some room.
    const clipEnd = Math.max(s.endTime + CLIP_PAD_SEC, clipStart + SEARCH_MIN_CLIP_SEC);
    return {
      source: 'transcript-search',
      searchQuery: query,
      startTime: s.startTime,
      endTime: s.endTime,
      clipStartTime: clipStart,
      clipEndTime: clipEnd,
      estimatedSizeMb: this.chat.calculateEstimatedSize(clipEnd - clipStart),
      timestamp: formatHms(s.startTime),
      sentiment: 'Нейтральный',
      description: s.description,
      sampleMessages: [],
      messageCount: this.countMessages(bins, s.startTime, s.endTime),
      isAnalyzed: true,
      isPinned: false,
      isDeleted: false,
      isPromoted: false,
      confidence: s.confidence,
      transcriptQuote: s.quote,
      transcriptLines: [s.startLine, s.endLine],
      transcriptModel: s.model,
      ...(s.withChat ? { transcriptWithChat: true } : {}),
    };
  }

  /** Remove non-pinned / non-promoted results of one source (the «✕» next to the counters). */
  // With a region active it clears what the counters show — results inside the region.
  clearResults(source: 'transcript-ai' | 'transcript-search'): void {
    const region = this.state.activeRegion();
    this.state.highlights.update(cur => cur.filter(h =>
      h.source !== source || h.isPinned || h.isPromoted || h.isDeleted || !isVisibleInRegion(h, region)));
    if (source === 'transcript-ai') { this.segRun.set(null); this.segCanRetry.set(false); this.segPending = null; }
    else { this.searchRun.set(null); this.searchCanRetry.set(false); this.searchPending = null; }
  }

  toggleCategory(cat: TranscriptCategory): void {
    this.state.speechCategoryFilter.update(cur => {
      const next = new Set(cur);
      if (next.has(cat)) next.delete(cat); else next.add(cat);
      // Never leave the list silently empty — clicking the last active chip re-enables all.
      return next.size === 0 ? new Set(TRANSCRIPT_CATEGORY_KEYS) : next;
    });
  }

  // ---------------------------------------------------------------------------
  // Plumbing
  // ---------------------------------------------------------------------------

  /** Segments plan: ~75-min windows over the whole transcript, or only the «область интереса». */
  private planRun(doc: TranscriptDoc, region: TimeRegion | null): TranscriptChunk[] {
    return region ? planCoresInRange(doc.lines, region.inSec, region.outSec) : planCores(doc.lines);
  }

  /** AI search plan: one request over the whole transcript / region (split only past ~a day of talk). */
  private planSearchRun(doc: TranscriptDoc, region: TimeRegion | null): TranscriptChunk[] {
    const [from, to] = region ? lineRangeIn(doc.lines, region.inSec, region.outSec) : [0, doc.lines.length];
    return planSearch(doc.lines, from, to);
  }

  /** Chunks can reach a little past the region's edges — keep only results touching it. */
  private inRegion<T extends { startTime: number; endTime: number }>(items: T[], region: TimeRegion | null): T[] {
    return region ? items.filter(s => overlapsRegion(s.startTime, s.endTime, region)) : items;
  }

  private captureContext() {
    // Twitch categories along the VOD (the heatmap zoom bar's chapters): videoGame is only the
    // FIRST category, so without them an evening in another game was analyzed as the first one.
    const ch = this.state.vodChapters();
    const chapters = ch && ch.videoId === String(this.state.videoId()) ? ch.chapters : [];
    return {
      streamer: this.state.streamerName() || 'Неизвестный',
      title: this.state.videoTitle() || 'Без названия',
      game: this.state.videoGame() || 'Неизвестная игра',
      ...(chapters.length ? { categories: categoriesPayload(chapters) } : {}),
    };
  }

  private countMessages(bins: ActivityBin[], start: number, end: number): number {
    const from = Math.floor(start / BIN_SEC) * BIN_SEC;
    let n = 0;
    for (const b of bins) if (b.startTime >= from && b.startTime <= end) n += b.messageCount;
    return n;
  }

  /**
   * One more chunk done: the run's progress is kept in its live entry and shown only while its VOD is
   * open (a run of another VOD keeps going in the background and must not touch this VOD's line).
   */
  private bumpRun(
    live: Map<string, LiveRun>, run: WritableSignal<TranscriptRunState | null>,
    videoId: string, runId: string, patch: Partial<TranscriptRunState>,
  ): void {
    const entry = live.get(videoId);
    if (!entry || entry.state.runId !== runId) return;
    entry.state = { ...entry.state, ...patch, done: Math.min(entry.state.total, entry.state.done + 1) };
    if (this.lastLoadedVideoId === videoId) run.set(entry.state);
  }

  private onTranscriptChanged(run: WritableSignal<TranscriptRunState | null>, runId: string, message: string): void {
    run.update(r => r && r.runId === runId ? { ...r, running: false, error: message } : r);
    this.reload();
  }

  /** Same-VOD → live signals (selection kept by startTime); other VOD → saved state + sync. */
  private async apply(
    videoId: string,
    incoming: Highlight[],
    source: 'transcript-ai' | 'transcript-search',
    scope: TimeRegion | null = null,
  ): Promise<void> {
    if (String(this.state.videoId()) === videoId) {
      const prevStart = this.state.currentHighlight()?.startTime;
      const before = this.state.highlights();
      this.state.highlights.update(cur => mergeTranscriptHighlights(cur, incoming, source, scope));
      this.state.onDocumentEvent?.({ kind: 'results-replaced', videoId, group: source, scope, before, after: this.state.highlights() });
      // Inserting shifts indices — currentHighlightIndex would silently point at another moment.
      if (prevStart != null) {
        const idx = this.state.highlights().findIndex(h => h.startTime === prevStart);
        if (idx >= 0) this.state.currentHighlightIndex.set(idx);
      }
      return;
    }
    try {
      const saved: SavedState | undefined = await this.library.getProjectState(videoId);
      if (!saved) {
        console.warn('[transcript background] saved state for VOD', videoId, 'not found, skipping persistence');
        return;
      }
      const updated: SavedState = { ...saved, highlights: mergeTranscriptHighlights(saved.highlights || [], incoming, source, scope), syncDirty: true };
      await this.library.saveProjectState(videoId, updated);
      this.sync.pushProjectState(videoId, updated);
      this.state.onDocumentEvent?.({
        kind: 'results-replaced', videoId, group: source, scope, before: saved.highlights || [], after: updated.highlights!,
      });
      console.info('[transcript background] persisted', source, 'for VOD', videoId);
    } catch (e) {
      console.error('[transcript background] failed to persist for VOD', videoId, e);
    }
  }

  private selectFirstIfNone(): void {
    const sorted = this.state.sortedHighlights();
    const cur = this.state.currentHighlight();
    if (sorted.length > 0 && !sorted.some(h => h.startTime === cur?.startTime)) {
      this.chat.selectHighlight(sorted[0].startTime);
    }
  }

  private async runPool<T>(items: T[], fn: (item: T) => Promise<void>): Promise<void> {
    let next = 0;
    const worker = async () => {
      while (next < items.length) {
        const item = items[next++];
        await fn(item);
      }
    };
    await Promise.all(Array.from({ length: Math.min(CHUNK_CONCURRENCY, items.length) }, worker));
  }

  /**
   * One chunk = one request. NO retry on network errors: a Cloud Run gateway timeout reaches the
   * browser as a CORS-less «Failed to fetch», and blindly re-sending would re-bill a long Gemini
   * call (the chat AI client's mistake we don't repeat). One delayed retry only when the server
   * says `retryable` (Gemini 503 / our 240s deadline).
   */
  private async postChunk(path: string, body: unknown, ctrl: AbortController, attempt = 1): Promise<any> {
    const headers = await this.firebase.buildCloudRunHeaders();
    if (!headers) throw new ChunkError('Нужно войти в аккаунт.', 401);
    const timeout = AbortSignal.timeout(CLIENT_TIMEOUT_MS);
    const signal = typeof (AbortSignal as any).any === 'function'
      ? (AbortSignal as any).any([ctrl.signal, timeout]) as AbortSignal
      : ctrl.signal;
    const res = await fetch(`${this.firebase.cloudRunApiUrl}${path}`, {
      method: 'POST',
      headers: { ...headers, 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      signal,
    });
    const payload = await res.json().catch(() => ({}));
    if (res.ok) return payload;
    const err = new ChunkError(payload.error || `HTTP ${res.status}`, res.status, payload.code, !!payload.retryable);
    if (err.retryable && attempt === 1 && !ctrl.signal.aborted) {
      await new Promise(r => setTimeout(r, RETRY_DELAY_MS));
      if (!ctrl.signal.aborted) return this.postChunk(path, body, ctrl, 2);
    }
    throw err;
  }
}
